import type { Cell, FileDef } from './types';
import { toast } from '@/hooks/use-toast';
import { save } from './save';

export interface DetailExportColumn {
  field: string;
  headerName: string;
  width?: number;
}

/** Reuse table labels/order, but explicitly select raw Excel values instead of rendered JSX. */
export const detailExportColumns = <T>(
  columns: DetailExportColumn[],
  getters: Record<string, (row: T) => Cell>
): FileDef<T>['cols'] =>
  columns.map(column => {
    const get = getters[column.field];
    if (!get) throw new Error(`Chưa cấu hình xuất Excel cho cột ${column.headerName}.`);
    return { header: column.headerName, width: Math.max(12, Math.round((column.width || 126) / 7)), get };
  });

export const detailExportName = (name: string, documentNumber?: string) => {
  const suffix = documentNumber
    ?.trim()
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return suffix ? `${name}-${suffix}` : name;
};

export interface DetailExportOptions<T> {
  file: FileDef<T>;
  documentNumber?: string;
  includeRow?: (row: T) => boolean;
  successMessage?: string;
  errorMessage?: string;
}

/** Export the current local detail state, including unsaved edits. */
export const exportDetails = async <T>(rows: T[], options: DetailExportOptions<T>): Promise<void> => {
  const exportRows = options.includeRow ? rows.filter(options.includeRow) : rows;
  if (exportRows.length === 0) {
    toast({ description: 'Không có dữ liệu chi tiết để kết xuất.' });
    return;
  }
  try {
    await save(exportRows, {
      ...options.file,
      name: detailExportName(options.file.name, options.documentNumber)
    });
    toast({ description: options.successMessage || 'Kết xuất chi tiết thành công.' });
  } catch (error) {
    console.error('Export details error:', error);
    toast({
      description: options.errorMessage || 'Không thể kết xuất chi tiết. Vui lòng thử lại.',
      variant: 'destructive'
    });
  }
};
